import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClinicLocationService } from '../services/clinic-location.service';
import { AssignPatientToClinicLocationRequest } from '../common/model/dto/assign-user-to-clinic-location-request';
import { CreateDemoAccountRequest } from '../common/model/dto/create-demo-account-request';

/**
 * Doctor/provider-side patient mutations (assign to clinic location and create demo account).
 * Uses the non-admin `ClinicLocationService` (the admin equivalent lives in `use-admin-clinic-location-mutations.ts`).
 *
 * Consumers handle their own UX (redirects, snackbars, and duplicate-invite messages) from the
 * returned mutation results.
 */
export function useClinicLocationPatientMutations(
  clinicLocationId: string | undefined
) {
  const qc = useQueryClient();

  const invalidatePatientList = () =>
    qc.invalidateQueries({ queryKey: ['clinic-location-patients'] });

  const assignPatientToClinicLocation = useMutation({
    mutationFn: (request: AssignPatientToClinicLocationRequest) =>
      ClinicLocationService.assignPatientToClinicLocation(
        clinicLocationId ?? '',
        request
      ),
    onSuccess: () => invalidatePatientList(),
  });

  const createDemoAccount = useMutation({
    mutationFn: (request: CreateDemoAccountRequest) =>
      ClinicLocationService.createDemoAccount(request, clinicLocationId),
    onSuccess: () => invalidatePatientList(),
  });

  return {
    assignPatientToClinicLocation,
    createDemoAccount,
  };
}
